import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { Controller, useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Grid } from '@material-ui/core'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { postNamedCommand } from '../../../../../service'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../commonDesign/Button'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydTextArea from '../../../../commonDesign/SydTextArea'
import Markup from '../../../../atoms/Markup'
import { htmlToPlainText } from './wealthboxHtml'
import { extractWealthboxErrorMessage } from './wealthboxErrors'

const MODE_VIEWING = 'viewing'
const MODE_EDITING = 'editing'
// NOTE: No MODE_CONFIRMING_DELETE - Wealthbox's /v1/notes endpoint does NOT
// expose a DELETE method. The SMT-1353 ticket text was wrong about that.
// If product wants delete, it needs to be filed with Wealthbox or implemented
// as content tombstoning.

const labelStyle = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  marginBottom: '4px'
}

const valueStyle = {
  fontSize: 'var(--font-size-sm)',
  marginBottom: '12px'
}

function buildFormDefaults (note) {
  return {
    // Wealthbox stores the note body under `content` (NOT `body` as the
    // SMT-1353 ticket text said). Strip the HTML wrapper for plaintext
    // editing; Wealthbox re-wraps on save.
    content: htmlToPlainText(note?.content || ''),
    // Notes don't have a `category` field on Wealthbox. We surface the
    // first-class `tags` array as a comma-separated string so admins can
    // edit it if they want.
    tagsCsv: Array.isArray(note?.tags)
      ? note.tags.map(t => (typeof t === 'string' ? t : t.name)).filter(Boolean).join(', ')
      : ''
  }
}

function ViewingBody ({ note }) {
  // Render all linked contacts (Wealthbox notes can be linked to multiple
  // contacts; the row preview only shows the first + "+N" hint).
  const linkedNames = Array.isArray(note.linked_to)
    ? note.linked_to.map(l => l?.name).filter(Boolean)
    : []
  const linkedLabel = linkedNames.length ? linkedNames.join(', ') : null
  const dateFormatted = note.created_at ? dayjs(note.created_at).format('ddd, MMM D, YYYY [at] h:mm A') : null
  const tagsLabel = Array.isArray(note.tags) && note.tags.length
    ? note.tags.map(t => (typeof t === 'string' ? t : t.name)).filter(Boolean).join(', ')
    : null

  return (
    <Grid container spacing={2}>
      {linkedLabel ? (
        <Grid item xs={12} sm={dateFormatted ? 6 : 12}>
          <div style={labelStyle}>{linkedNames.length > 1 ? 'Linked Contacts' : 'Linked Contact'}</div>
          <div style={valueStyle}>{linkedLabel}</div>
        </Grid>
      ) : null}
      {dateFormatted ? (
        <Grid item xs={12} sm={linkedLabel ? 6 : 12}>
          <div style={labelStyle}>Created</div>
          <div style={valueStyle}>{dateFormatted}</div>
        </Grid>
      ) : null}
      {tagsLabel ? (
        <Grid item xs={12}>
          <div style={labelStyle}>Tags</div>
          <div style={valueStyle}>{tagsLabel}</div>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <div style={labelStyle}>Note</div>
        {note.content
          ? (
            // Wealthbox stores notes as HTML - auto-wrapping plaintext sent
            // over the API in `<div>` tags and preserving any rich markup
            // (links, lists, line breaks) the user added in Wealthbox's UI.
            // Render through Markup so DOMPurify sanitizes before the HTML
            // is injected into the DOM.
            <Markup
              value={note.content}
              type='html'
              style={{ ...valueStyle, fontWeight: 'inherit' }}
            />
          )
          : (
            <div style={{ ...valueStyle, color: 'var(--text-color-subtle)', fontStyle: 'italic' }}>Empty note</div>
          )}
      </Grid>
    </Grid>
  )
}

ViewingBody.propTypes = {
  note: PropTypes.object.isRequired
}

function EditingBody ({ note, onSubmit, onCancel }) {
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const { control, handleSubmit } = useForm({ defaultValues: buildFormDefaults(note) })

  const submit = handleSubmit(async (values) => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      // Wealthbox PUT /v1/notes/{id} accepts partial updates. Field names:
      // `content` (body), `tags` (string array). No `category` field exists.
      const body = { content: values.content }
      const parsedTags = (values.tagsCsv || '')
        .split(',')
        .map(t => t.trim())
        .filter(Boolean)
      body.tags = parsedTags // Always send (empty array clears)
      await onSubmit(body)
    } catch (err) {
      setSubmitError(extractWealthboxErrorMessage(err, 'Failed to save note'))
    } finally {
      setSubmitting(false)
    }
  })

  return (
    <form onSubmit={submit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name='content'
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <SydLabel label='Note' required>
                <SydTextArea {...field} value={field.value || ''} rows={6} placeholder='Note content' />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name='tagsCsv'
            control={control}
            render={({ field }) => (
              <SydLabel
                label='Tags'
                description='Separate multiple tags with commas.'
              >
                <SydTextArea {...field} value={field.value || ''} rows={1} placeholder='Clients, VIP' />
              </SydLabel>
            )}
          />
        </Grid>
      </Grid>

      {submitError ? (
        <div className='font-size-xs mt-2' style={{ color: 'var(--color-decline-500)' }}>{submitError}</div>
      ) : null}

      <SydModalActions>
        <SydButton variant='ghost' size='sm' onClick={onCancel} disabled={submitting} type='button'>Cancel</SydButton>
        <SydButton variant='primary' size='sm' processing={submitting} type='submit'>Save Changes</SydButton>
      </SydModalActions>
    </form>
  )
}

EditingBody.propTypes = {
  note: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

function WealthboxNoteDetailModal ({ open, onClose, note, enableEdit = false, invalidateQueryKeys }) {
  const { clientId } = useAppContext()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState(MODE_VIEWING)

  useEffect(() => { if (open) setMode(MODE_VIEWING) }, [open, note?.id])

  const invalidateAll = useCallback(() => {
    if (!invalidateQueryKeys) return
    for (const key of invalidateQueryKeys) {
      queryClient.invalidateQueries({ queryKey: key })
    }
  }, [invalidateQueryKeys, queryClient])

  const { mutateAsync: handleEditSubmit } = useMutation({
    mutationFn: (body) => postNamedCommand('coreData', 'execute-wealthbox-domain-command', {
      command: 'modifyNote',
      clientId,
      request: { noteId: note.id, body }
    }),
    onSuccess: () => { onClose(); invalidateAll() }
  })

  if (!note) return null

  const titles = {
    [MODE_VIEWING]: 'Note',
    [MODE_EDITING]: 'Edit Note'
  }

  return (
    <SydModal open={open} onClose={onClose} title={titles[mode]} size='xmedium' disablePortal={false}>
      {mode === MODE_VIEWING && (
        <>
          <ViewingBody note={note} />
          <SydModalActions>
            <div style={{ flex: 1 }} />
            <SydButton variant='ghost' size='sm' onClick={onClose}>Close</SydButton>
            {enableEdit ? (
              <SydButton variant='primary' size='sm' icon='edit' onClick={() => setMode(MODE_EDITING)}>Edit</SydButton>
            ) : null}
          </SydModalActions>
        </>
      )}

      {mode === MODE_EDITING && (
        <EditingBody
          note={note}
          onSubmit={handleEditSubmit}
          onCancel={() => setMode(MODE_VIEWING)}
        />
      )}
    </SydModal>
  )
}

WealthboxNoteDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  note: PropTypes.object,
  enableEdit: PropTypes.bool,
  invalidateQueryKeys: PropTypes.arrayOf(PropTypes.array)
}

export default WealthboxNoteDetailModal
